import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import SvgPreview from './SvgPreview';
import DesignTextMapper from './DesignTextMapper';
import SheetLayoutPanel from './SheetLayoutPanel';

const scanTokens = (txt) => [...new Set([...txt.matchAll(/\{\{\s*([A-Za-z0-9_]+)\s*\}\}/g)].map(m => m[1].toUpperCase()))];

export const FaceplateTemplateDialog = ({ api, open, onOpenChange, template, builtins, productOptions, onSaved }) => {
  const [name, setName] = useState('');
  const [keys, setKeys] = useState('');
  const [file, setFile] = useState(null);
  const [fields, setFields] = useState([]);
  const [busy, setBusy] = useState(false);
  const [ver, setVer] = useState(0);
  useEffect(() => {
    if (!open) return;
    setName(template?.name || ''); setKeys((template?.product_keys || []).join(', ')); setFile(null); setFields(template?.fields || []);
  }, [open, template]);

  const onFile = async (f) => {
    setFile(f);
    if (!f) return;
    const toks = scanTokens(await f.text()).filter(t => !(t in (builtins || {})));
    setFields(prev => toks.map(k => prev.find(x => x.key === k) || { key: k, label: k.replace(/_/g, ' ').toLowerCase().replace(/^\w/, c => c.toUpperCase()), default: '', scope: 'run', required: false }));
    if (!name) setName(f.name.replace(/\.svg$/i, ''));
  };
  const setF = (i, patch) => setFields(fs => fs.map((x, j) => (j === i ? { ...x, ...patch } : x)));

  const save = async () => {
    if (!name.trim()) { toast.error('Name is required'); return; }
    if (!template && !file) { toast.error('Upload your SVG design'); return; }
    const fd = new FormData();
    fd.append('name', name); fd.append('product_keys', keys); fd.append('fields', JSON.stringify(fields));
    if (file) fd.append('file', file);
    setBusy(true);
    try {
      const r = template ? await api().put(`/faceplates/${template.id}`, fd) : await api().post('/faceplates', fd);
      toast.success(`Saved ${r.data.name} (v${r.data.version})`); onSaved(r.data); setVer(v => v + 1);
      if (!template) onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="faceplate-template-dialog">
        <DialogHeader><DialogTitle>{template ? `Edit faceplate · v${template.version}` : 'New faceplate template'}</DialogTitle></DialogHeader>
        <div className="grid md:grid-cols-2 gap-3">
          <div><Label>Name *</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="rounded-sm" data-testid="fp-name-input" /></div>
          <div>
            <Label>Applies to products (SKU / product, comma-separated)</Label>
            <Input value={keys} onChange={(e) => setKeys(e.target.value)} list="fp-product-options" placeholder="PUMP-500, 11 FT" className="rounded-sm mono" data-testid="fp-products-input" />
            <datalist id="fp-product-options">{(productOptions || []).map(p => <option key={p} value={p} />)}</datalist>
          </div>
        </div>
        <div>
          <Label>{template ? 'Replace SVG design (optional, creates a new version)' : 'SVG design file *'}</Label>
          <input type="file" accept=".svg,image/svg+xml" onChange={(e) => onFile(e.target.files?.[0] || null)} className="block text-sm mt-1" data-testid="fp-file-input" />
          <p className="text-[11px] text-slate-500 mt-1">Put placeholders in your design text like <span className="mono">{'{{SERIAL}}'}</span>, <span className="mono">{'{{DATE}}'}</span> or your own like <span className="mono">{'{{VOLTAGE}}'}</span>. For a QR code use an image with <span className="mono">{'href="{{QR}}"'}</span>. Convert text to paths only for parts that never change.</p>
        </div>
        <div className="text-xs text-slate-600" data-testid="fp-builtins">
          <span className="font-semibold">Auto-filled:</span> {Object.keys(builtins || {}).map(k => <span key={k} className="mono inline-block bg-slate-100 rounded-sm px-1 mr-1 mb-1" title={builtins[k]}>{k}</span>)}
        </div>
        <div>
          <p className="text-sm font-semibold mb-1">Custom fields found in the design ({fields.length})</p>
          {fields.length === 0 ? <p className="text-xs text-slate-400">No custom placeholders. Only auto-filled values will be used.</p> : (
            <table className="w-full text-sm" data-testid="fp-fields-table">
              <thead><tr className="text-[10px] uppercase text-slate-500 text-left"><th className="py-1">Placeholder</th><th>Label</th><th>Default</th><th>Filled</th><th>Required</th></tr></thead>
              <tbody>{fields.map((f, i) => (
                <tr key={f.key} className="border-t border-slate-100" data-testid={`fp-field-row-${f.key}`}>
                  <td className="mono text-xs py-1">{f.key}</td>
                  <td><Input value={f.label} onChange={(e) => setF(i, { label: e.target.value })} className="h-8 rounded-sm" data-testid={`fp-field-label-${f.key}`} /></td>
                  <td><Input value={f.default} onChange={(e) => setF(i, { default: e.target.value })} className="h-8 rounded-sm" data-testid={`fp-field-default-${f.key}`} /></td>
                  <td><select value={f.scope} onChange={(e) => setF(i, { scope: e.target.value })} className="h-8 border border-slate-200 rounded-sm text-xs px-1" data-testid={`fp-field-scope-${f.key}`}><option value="run">Once per run</option><option value="unit">Per unit</option></select></td>
                  <td className="text-center"><input type="checkbox" checked={!!f.required} onChange={(e) => setF(i, { required: e.target.checked })} data-testid={`fp-field-required-${f.key}`} /></td>
                </tr>))}</tbody>
            </table>
          )}
        </div>
        {template && <DesignTextMapper api={api} template={template} builtins={builtins} onChanged={(t) => { setFields(t.fields); onSaved(t); setVer(v => v + 1); }} />}
        {template && <div><p className="text-sm font-semibold mb-1">Preview (sample values)</p><SvgPreview api={api} url={`/faceplates/${template.id}/preview`} method="post" refreshKey={`${template.version}-${ver}`} testId="fp-template-preview" /></div>}
        {template && <SheetLayoutPanel api={api} template={template} onSaved={onSaved} />}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="fp-cancel-btn">Close</Button>
          <Button onClick={save} disabled={busy} className="bg-slate-900 hover:bg-slate-800 rounded-sm" data-testid="fp-save-btn">{busy ? 'Saving…' : 'Save template'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default FaceplateTemplateDialog;
